import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GetAllAssetsParams, VelavuAPI, VelavuAsset } from "velavu-js-api";
import { useGlobalEmitter } from "../emitter/emitter-context";
import { useSubscribeCombinedAssetUpdates } from "../emitter/stream-helper";

interface InternalListAssetsState {
	assets: VelavuAsset[];
	isLoading: boolean;
	error: unknown | null;
	continuationToken: string | null;
}

export interface ListAssetsState {
	assets: VelavuAsset[];
	isLoading: boolean;
	error: unknown | null;
	hasMore: boolean;
}

function internalListAssetsStateToSuccessState(
	state: InternalListAssetsState,
): ListAssetsState {
	return {
		assets: state.assets,
		isLoading: state.isLoading,
		error: state.error,
		hasMore: state.continuationToken !== null,
	};
}

export interface ListAssetsActions {
	loadAssets: (params?: GetAllAssetsParams) => Promise<void>;
	loadMore: () => Promise<void>;
	deleteAsset: (assetID: string) => void;
}

export function useListAssetsState(): [ListAssetsState, ListAssetsActions] {
	const emitter = useGlobalEmitter();

	const [assetsState, setAssetsState] = useState<InternalListAssetsState>({
		assets: [],
		isLoading: false,
		error: null,
		continuationToken: null,
	});

	const activeRequestID = useRef(0);
	const initialRequestParams = useRef<GetAllAssetsParams | undefined | null>(
		null,
	);

	const loadAssets = useCallback(
		async (params?: GetAllAssetsParams) => {
			//Get a request ID
			activeRequestID.current += 1;
			const requestID = activeRequestID.current;

			//Reset the assets state
			setAssetsState((state) => ({
				...state,
				isLoading: true,
			}));

			//Store the params
			initialRequestParams.current = params;

			//Load the assets
			try {
				const allAssets = await VelavuAPI.assets.getAllAssets(params);

				//Make sure our request is still active
				if (activeRequestID.current === requestID) {
					setAssetsState({
						assets: allAssets.data,
						isLoading: false,
						error: null,
						continuationToken: allAssets.continuationToken ?? null,
					});
				}
			} catch (error) {
				//Make sure our request is still active
				if (activeRequestID.current === requestID) {
					setAssetsState({
						assets: [],
						isLoading: false,
						error,
						continuationToken: null,
					});
				}

				//Rethrow the error
				throw error;
			}
		},
		[setAssetsState],
	);

	const loadMore = useCallback(async () => {
		//Check state
		if (assetsState.isLoading) {
			console.warn("Attempted to load more assets while already loading");
			return;
		}
		if (assetsState.continuationToken === null) {
			console.warn(
				"Attempted to load more assets with no continuation token",
			);
			return;
		}
		if (initialRequestParams.current === null) {
			console.warn(
				"Attempted to load more assets without initial request params",
			);
			return;
		}

		//Reset the assets state
		const newAssetsState: Readonly<InternalListAssetsState> = {
			...assetsState,
			isLoading: true,
			error: null,
		};
		setAssetsState(newAssetsState);

		//Load the assets
		try {
			const moreAssets = await VelavuAPI.assets.getAllAssets(
				initialRequestParams.current,
				{
					continuationToken: assetsState.continuationToken,
				},
			);
			setAssetsState({
				...newAssetsState,
				isLoading: false,
				assets: [...newAssetsState.assets, ...moreAssets.data],
				continuationToken: moreAssets.continuationToken ?? null,
			});
		} catch (error) {
			setAssetsState({
				...newAssetsState,
				isLoading: false,
				error,
			});

			//Rethrow the error
			throw error;
		}
	}, [assetsState, setAssetsState]);

	const deleteAsset = useCallback(
		async (assetID: string) => {
			try {
				await VelavuAPI.assets.deleteSpecificAsset(assetID);
				emitter.emit("asset", {
					id: assetID,
					data: null,
				});
			} catch (error) {
				console.log(error);
			}
		},
		[emitter],
	);

	//Subscribe to remote and locally emitted asset updates
	useSubscribeCombinedAssetUpdates(
		useCallback((update) => {
			setAssetsState((assetsState) => {
				//Try to find the asset in the list
				const assetIndex = assetsState.assets.findIndex(
					(asset) => asset.id === update.id,
				);
				if (assetIndex === -1) {
					return assetsState;
				}

				//Apply the update
				const updatedAssets = [...assetsState.assets];
				if (update.data === null) {
					//Remove the deleted asset
					updatedAssets.splice(assetIndex, 1);
				} else {
					//Apply the update
					updatedAssets[assetIndex] = update.data;
				}
				return {
					...assetsState,
					assets: updatedAssets,
				};
			});
		}, []),
	);

	useEffect(() => {
		//Load the initial assets
		loadAssets();
	}, []); //eslint-disable-line react-hooks/exhaustive-deps

	//Convert the internal state to public state
	const publicAssetssState = useMemo(() => {
		return internalListAssetsStateToSuccessState(assetsState);
	}, [assetsState]);

	//Memoize the actions
	const actions = useMemo<ListAssetsActions>(() => {
		return {
			loadAssets,
			loadMore,
			deleteAsset,
		};
	}, [deleteAsset, loadAssets, loadMore]);

	return [publicAssetssState, actions];
}
